import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { db } from '../../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { FaWhatsapp, FaCopy, FaCheck } from 'react-icons/fa';

const RetryPaymentModal = ({ app, onClose, onSuccess }) => {
  const [dynamicPrice, setDynamicPrice] = useState(119.90);
  const [copiedId, setCopiedId] = useState(false);

  useEffect(() => {
    if (!Number(app?.totalPrice)) {
      const docRef = doc(db, 'settings', 'pricing');
      getDoc(docRef)
        .then((docSnap) => {
          if (docSnap.exists()) {
            const basePrice = docSnap.data().base;
            setDynamicPrice(basePrice !== undefined && basePrice !== null ? basePrice : 119.90);
          }
        })
        .catch((err) => console.log('Erro ao buscar pricing:', err));
    }
  }, [app?.totalPrice]);

  // Fechar no ESC
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  if (!app) return null;

  const parsedPrice = Number(app.totalPrice);
  const amountToPay = !isNaN(parsedPrice) && app.totalPrice !== undefined && app.totalPrice !== null ? parsedPrice : dynamicPrice;

  const whatsappMessage = `Olá! Gostaria de efetuar o pagamento da fatura do meu aplicativo *${app.name || 'Meu App'}* (ID: *${app.id}*). Valor: *R$ ${amountToPay.toFixed(2)}*. Como posso prosseguir com o pagamento?`;
  const whatsappUrl = `https://wa.me/5582996992182?text=${encodeURIComponent(whatsappMessage)}`;

  const handleCopyId = () => {
    if (app.id) {
      navigator.clipboard.writeText(app.id);
      setCopiedId(true);
      setTimeout(() => setCopiedId(false), 2000);
    }
  };

  const handleOpenWhatsApp = () => {
    window.open(whatsappUrl, '_blank');
  };

  return createPortal(
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100] p-4 backdrop-blur-sm overflow-y-auto animate-fade-in">
      <div className="bg-white dark:bg-gray-800 p-6 sm:p-8 rounded-2xl shadow-2xl max-w-lg w-full flex flex-col mx-auto relative animate-slide-up border border-gray-100 dark:border-gray-700">
        
        {/* Botão Fechar */}
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 dark:hover:text-white transition-colors p-1 rounded-lg"
          aria-label="Fechar"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Cabeçalho */}
        <div className="mb-6">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300 mb-3">
            <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse"></span>
            Aguardando Pagamento
          </div>
          <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
            Pagamento do Aplicativo
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
            Conclua o pagamento para liberar o desenvolvimento e publicação do seu app.
          </p>
        </div>

        {/* Card com Detalhes da Fatura */}
        <div className="bg-gray-50 dark:bg-gray-900/60 rounded-xl p-4 sm:p-5 mb-6 border border-gray-100 dark:border-gray-700/60 space-y-3">
          <div className="flex justify-between items-center pb-2 border-b border-gray-200 dark:border-gray-700">
            <span className="text-sm text-gray-500 dark:text-gray-400">Aplicativo:</span>
            <span className="text-sm font-bold text-gray-900 dark:text-white">{app.name || 'Sem Nome'}</span>
          </div>

          <div className="flex justify-between items-center pb-2 border-b border-gray-200 dark:border-gray-700">
            <span className="text-sm text-gray-500 dark:text-gray-400">ID do Pedido:</span>
            <div className="flex items-center gap-2">
              <span className="text-xs font-mono font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 px-2 py-0.5 rounded border border-gray-200 dark:border-gray-700 max-w-[150px] truncate">
                {app.id}
              </span>
              <button
                onClick={handleCopyId}
                className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 p-1"
                title="Copiar ID"
              >
                {copiedId ? <FaCheck className="text-green-500" /> : <FaCopy />}
              </button>
            </div>
          </div>

          <div className="flex justify-between items-center pt-1">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Valor da Fatura:</span>
            <span className="text-xl font-extrabold text-gray-900 dark:text-white">
              R$ {amountToPay.toFixed(2)}
            </span>
          </div>
        </div>

        {/* Informações sobre o atendimento */}
        <div className="bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800/40 rounded-xl p-4 mb-6 text-sm text-green-900 dark:text-green-300">
          <p className="font-semibold flex items-center gap-2 mb-1">
            <FaWhatsapp className="text-green-600 dark:text-green-400 text-lg flex-shrink-0" />
            Pagamento Ágil via WhatsApp
          </p>
          <p className="text-xs text-green-800 dark:text-green-400/90 leading-relaxed">
            Clique no botão abaixo para falar com nosso time de atendimento. Você receberá a chave PIX ou link de pagamento e, ao enviar o comprovante, seu app é liberado imediatamente!
          </p>
        </div>

        {/* Botões de Ação */}
        <div className="space-y-3">
          <button
            onClick={handleOpenWhatsApp}
            className="w-full py-3.5 px-4 bg-[#25D366] hover:bg-[#20bd5a] text-white font-bold rounded-xl shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-2 text-base active:scale-[0.99]"
          >
            <FaWhatsapp className="text-xl" />
            <span>Falar no WhatsApp / Pagar</span>
          </button>

          <button
            onClick={onClose}
            className="w-full py-3 px-4 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 font-semibold rounded-xl transition-colors text-sm"
          >
            Fechar
          </button>
        </div>

      </div>
    </div>,
    document.body
  );
};

export default RetryPaymentModal;
